// Human-interaction slice: the human-in-the-loop turn drivers that pause and
// resume an agent run — ask_human answers, credential-setup completion,
// guide_human wizard steps, and human-takeover start/end. These all share the
// "resume a paused session over the existing SSE" branch and are kept together,
// separate from the core send/start drivers in the conversation slice.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import {
  send_request,
} from "../../utils/Request";
import {
  log as _log,
} from "../../utils/ChatStreamLogger";
import {
  STATUS_IN_PROGRESS,
  extractApiStatusCode,
  extractApiPayload,
  resolveApiErrorMessage,
  _extractBlockedIngressPayload,
  _buildBlockedIngressDetailPatch,
} from "./agentStoreUtils";

// Shared "resume a paused session over the existing SSE" branch used by the
// ask_human / credential-setup / guide_human resume drivers. When the resumed
// session is the one being viewed, optimistically flip it to IN_PROGRESS (the
// backend resumes the loop to exactly this state) before subscribing, and force
// a reconnect since the prior turn's stream closed when the session paused. When
// a DIFFERENT session is viewed (answering a worker question from inside a team
// chat), keep the PARENT stream attached without switching the subscription or
// forcing a reconnect — only one session streams at a time, and the worker's
// resumed activity is relayed via team_child_activity. `onWorkerResubscribe`
// runs after the worker-branch resubscribe (e.g. to flip an embedded card).
function _resumePausedSession(get, set, chatSessionId, { onWorkerResubscribe } = {}) {
  const viewedSessionId = get().sessionDetail?.chatSessionId;
  if (viewedSessionId === chatSessionId) {
    // Resume opener: flip chatStatus→submitted and open a fresh turn epoch
    // (appliedSseSeq→0, terminalApplied→false) in lockstep with the forced
    // resubscribe below, so the replayed snapshot/events of the resumed loop
    // rebuild from zero instead of being rejected as stale. The reducer is the
    // sole chatStatus writer.
    get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'resume' });
    set((s) =>
      s.sessionDetail?.chatSessionId === chatSessionId
        ? { sessionDetail: { ...s.sessionDetail, status: STATUS_IN_PROGRESS } }
        : {}
    );
    get().subscribeToSession(chatSessionId, {
      reconnectWhileSubscribed: true,
      forceReconnect: true,
    });
  } else if (viewedSessionId) {
    get().subscribeToSession(viewedSessionId, {
      reconnectWhileSubscribed: true,
    });
    if (onWorkerResubscribe) onWorkerResubscribe();
  }
}

export function createHumanInteractionSlice(set, get) {
  return {
  answerHumanQuestion: async (
    chatSessionId,
    agentId,
    { questionId, selectedOptionIndices = [], freeText = null } = {}
  ) => {
    if (!chatSessionId || !questionId) return false;
    try {
      const payload = {
        agentId,
        questionId,
        selectedOptionIndices: Array.isArray(selectedOptionIndices)
          ? selectedOptionIndices
          : [],
        freeText: freeText || null,
      };
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/answer-question`,
        payload,
        {},
        "POST"
      );
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        return false;
      }
      // The relay won't re-emit the answered ask_human part, so when a worker
      // question is answered from inside a team chat, flip the embedded worker
      // card to its ANSWERED state now instead of leaving it spinning until the
      // run settles.
      _resumePausedSession(get, set, chatSessionId, {
        onWorkerResubscribe: () =>
          get()._markTeamAskHumanAnswered(questionId, {
            status: "ANSWERED",
            selectedOptionIndices: Array.isArray(selectedOptionIndices)
              ? selectedOptionIndices
              : [],
            freeText: freeText || null,
          }),
      });
      if (agentId) {
        get().bumpAgentLastActivity(agentId);
      }
      return true;
    } catch (e) {
      _log("Send", `answerHumanQuestion failed: ${e?.message || e}`);
      return false;
    }
  },

  /**
   * Records the completion of a `request_credential_setup` request (the user created a secret or
   * skipped) and re-subscribes so the blocked agent loop resumes over the existing SSE — the
   * original tool part flips to its COMPLETED state and a status_changed event unlocks the composer.
   * Mirrors {@link answerHumanQuestion} but for the credential-setup island. Returns true on
   * success, false on failure (the inline card surfaces the error).
   */
  completeCredentialSetup: async (
    chatSessionId,
    agentId,
    { setupId, outcome = "created", credentialId = null } = {}
  ) => {
    if (!chatSessionId || !setupId) return false;
    try {
      const payload = {
        agentId,
        setupId,
        outcome,
        credentialId: credentialId || null,
      };
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/credential-setup-complete`,
        payload,
        {},
        "POST"
      );
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        return false;
      }
      // Unlike ask_human there is no embedded worker card to flip here:
      // request_credential_setup is not currently rendered inside team-child
      // transcripts, so the worker branch has no _markTeam* equivalent.
      _resumePausedSession(get, set, chatSessionId);
      if (agentId) {
        get().bumpAgentLastActivity(agentId);
      }
      return true;
    } catch (e) {
      _log("Send", `completeCredentialSetup failed: ${e?.message || e}`);
      return false;
    }
  },

  // Loads the persisted guide_human snapshot from Mongo (survives reload; intermediate advances
  // are not written back to the tool part envelope until complete/cancel).
  fetchHumanGuideSnapshot: async (chatSessionId, guideId) => {
    if (!chatSessionId || !guideId) return null;
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/guide/${encodeURIComponent(guideId)}`,
        null,
        {},
        "GET"
      );
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        return null;
      }
      return extractApiPayload(response);
    } catch (e) {
      _log("Send", `fetchHumanGuideSnapshot failed: ${e?.message || e}`);
      return null;
    }
  },

  // Records one completed step of a guide_human wizard. Persists progress WITHOUT resuming the
  // loop, so it neither flips session status nor (re)subscribes — it just returns the next-step
  // snapshot ({ guideId, title, steps, currentStepIndex, stepValues }) the card renders, or false.
  advanceHumanGuide: async (chatSessionId, { guideId, stepId, value = null } = {}) => {
    if (!chatSessionId || !guideId || !stepId) return false;
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/guide-advance`,
        { guideId, stepId, value },
        {},
        "POST"
      );
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        _log(
          "Send",
          `advanceHumanGuide failed (${statusCode}): ${resolveApiErrorMessage(response, "advance rejected")}`
        );
        return false;
      }
      const snapshot = extractApiPayload(response);
      return snapshot || false;
    } catch (e) {
      _log(
        "Send",
        `advanceHumanGuide failed: ${resolveApiErrorMessage(e, e?.message || e)}`
      );
      return false;
    }
  },

  // Finishes (completes or cancels) a guide_human wizard. Mirrors completeCredentialSetup: resumes
  // the paused loop, so optimistically flip the viewed session to IN_PROGRESS and force a stream
  // reconnect. `cancel` selects the guide-cancel endpoint.
  _finishHumanGuide: async (chatSessionId, agentId, guideId, cancel) => {
    if (!chatSessionId || !guideId) return false;
    try {
      const endpoint = cancel ? "guide-cancel" : "guide-complete";
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/${endpoint}`,
        { agentId, guideId },
        {},
        "POST"
      );
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        return false;
      }
      _resumePausedSession(get, set, chatSessionId);
      if (agentId) {
        get().bumpAgentLastActivity(agentId);
      }
      return true;
    } catch (e) {
      _log("Send", `finishHumanGuide failed: ${e?.message || e}`);
      return false;
    }
  },

  completeHumanGuide: async (chatSessionId, agentId, { guideId } = {}) =>
    get()._finishHumanGuide(chatSessionId, agentId, guideId, false),

  cancelHumanGuide: async (chatSessionId, agentId, { guideId } = {}) =>
    get()._finishHumanGuide(chatSessionId, agentId, guideId, true),

  // Shared driver for start/end human-takeover. They differ only by endpoint
  // suffix, the status to assume when the response omits one, and the error
  // copy; everything else (optimistic status patch, authoritative REST refetch,
  // list refresh, 409 blocked-ingress handling) is identical.
  _setHumanTakeover: async (chatSessionId, { endpoint, fallbackStatus, errorMessage }) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/human-takeover/${endpoint}`,
        {},
        {},
        "POST"
      );
      const data = response?.data || null;
      set((s) =>
        s.sessionDetail?.chatSessionId === chatSessionId
          ? {
              sessionDetail: {
                ...s.sessionDetail,
                status: data?.status || fallbackStatus,
                statusReason: null,
              },
            }
          : {}
      );
      await get().fetchSessionDetail(chatSessionId, { silent: true });
      get().refreshSessionInList(chatSessionId);
      return data;
    } catch (e) {
      const blockedPayload = _extractBlockedIngressPayload(e);
      if (blockedPayload) {
        set((s) => ({
          sessionDetail: _buildBlockedIngressDetailPatch(s.sessionDetail, chatSessionId, blockedPayload),
        }));
        get().refreshSessionInList(chatSessionId);
        return { blocked: true, ...blockedPayload };
      }
      set({ error: resolveApiErrorMessage(e, errorMessage) });
      return null;
    }
  },

  startHumanTakeover: (chatSessionId) =>
    get()._setHumanTakeover(chatSessionId, {
      endpoint: "start",
      fallbackStatus: "HUMAN_TAKEOVER",
      errorMessage: "Failed to start human takeover",
    }),

  endHumanTakeover: (chatSessionId) =>
    get()._setHumanTakeover(chatSessionId, {
      endpoint: "end",
      fallbackStatus: "COMPLETED",
      errorMessage: "Failed to end human takeover",
    }),
  };
}
